import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRY_OPTIONS,
  buildPhoneStorageValue,
  findPhoneCountryOption,
  parseStoredPhoneValue
} from '../../utils/phoneUtils';

const PhoneInput = ({
  label,
  name,
  value,
  onChange,
  error = '',
  required = false,
  disabled = false,
  helperText = '',
  id,
  defaultCountry = DEFAULT_PHONE_COUNTRY,
  placeholder = 'Enter phone number',
  className = ''
}) => {
  const [countryQuery, setCountryQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(findPhoneCountryOption(defaultCountry));
  const [nationalNumber, setNationalNumber] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const lastEmittedValueRef = useRef();

  const syncFromValue = (nextValue) => {
    const parsed = parseStoredPhoneValue(nextValue, defaultCountry);
    setSelectedCountry(findPhoneCountryOption(parsed.country || defaultCountry));
    setNationalNumber(parsed.displayNationalNumber || parsed.nationalNumber || '');
  };

  useEffect(() => {
    syncFromValue(value);
    lastEmittedValueRef.current = value;
  }, []);

  useEffect(() => {
    if (value === lastEmittedValueRef.current) {
      return;
    }

    syncFromValue(value);
    lastEmittedValueRef.current = value;
  }, [defaultCountry, value]);

  useEffect(() => {
    if (!dropdownOpen) {
      return undefined;
    }

    searchInputRef.current?.focus();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setCountryQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const emitChange = (nextCountry, nextNationalNumber) => {
    const { value: nextValue } = buildPhoneStorageValue({
      country: nextCountry?.isoCode || defaultCountry,
      nationalNumber: nextNationalNumber
    });

    lastEmittedValueRef.current = nextValue;
    onChange?.({
      target: {
        name,
        value: nextValue
      }
    });
  };

  const filteredCountries = countryQuery.trim()
    ? PHONE_COUNTRY_OPTIONS.filter((option) => option.searchText.includes(countryQuery.trim().toLowerCase()))
    : PHONE_COUNTRY_OPTIONS;

  const selectedCountryDialCode = selectedCountry?.dialCode ? `+${selectedCountry.dialCode}` : '';
  const numberPlaceholder = placeholder === 'Enter phone number'
    ? selectedCountry?.isoCode === 'LK'
      ? '71 234 5678'
      : 'Enter phone number'
    : placeholder;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id || name} className="mb-1 block text-sm font-semibold text-gray-800">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative" ref={dropdownRef}>
        <div
          className={`flex w-full overflow-hidden rounded-lg border-0 bg-gray-50 text-sm transition-all ${
            error
              ? 'ring-2 ring-red-500 bg-red-50'
              : 'focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-500'
          } ${disabled ? 'opacity-60' : ''}`}
        >
          <div className="relative shrink-0 border-r border-gray-200/80">
            <button
              type="button"
              onClick={() => {
                if (disabled) {
                  return;
                }

                setDropdownOpen((current) => {
                  const nextOpen = !current;
                  if (!nextOpen) {
                    setCountryQuery('');
                  }
                  return nextOpen;
                });
              }}
              className="flex h-full min-w-[6.75rem] items-center gap-2 px-3 py-2 text-left text-gray-700 transition-colors hover:bg-gray-100/70 focus:outline-none disabled:cursor-not-allowed"
              aria-label="Select country code"
              aria-expanded={dropdownOpen}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {selectedCountry?.isoCode || defaultCountry}
              </span>
              <span className="text-sm font-medium text-gray-800">{selectedCountryDialCode}</span>
              <ChevronUpDownIcon
                className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
          </div>

          <div className="min-w-0 flex-1">
            <input
              type="tel"
              id={id || name}
              name={name}
              value={nationalNumber}
              onChange={(event) => {
                const nextValue = event.target.value;
                setNationalNumber(nextValue);
                emitChange(selectedCountry, nextValue);
              }}
              placeholder={numberPlaceholder}
              disabled={disabled}
              className="h-full w-full border-0 bg-transparent px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed"
              required={required}
              autoComplete="tel-national"
            />
          </div>
        </div>

        {dropdownOpen && (
          <div className="absolute left-0 z-20 mt-1 w-full min-w-[18rem] max-w-[24rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={countryQuery}
                  onChange={(event) => setCountryQuery(event.target.value)}
                  placeholder="Search country or code"
                  className="w-full rounded-lg border-0 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto py-1">
              {filteredCountries.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-500">No countries found</div>
              ) : (
                filteredCountries.map((option) => {
                  const isSelected = option.isoCode === selectedCountry?.isoCode;

                  return (
                    <button
                      key={option.isoCode}
                      type="button"
                      onClick={() => {
                        setSelectedCountry(option);
                        setCountryQuery('');
                        setDropdownOpen(false);
                        emitChange(option, nationalNumber);
                      }}
                      className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                        isSelected ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900 hover:bg-indigo-50'
                      }`}
                    >
                      <span className="min-w-[2.25rem] text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        {option.isoCode}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-inherit">{option.name}</p>
                        <p className="text-xs text-gray-500">+{option.dialCode}</p>
                      </div>
                      {isSelected && <CheckIcon className="h-4 w-4 flex-shrink-0 text-indigo-600" aria-hidden="true" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : helperText ? (
        <p className="mt-1 text-xs text-gray-500">{helperText}</p>
      ) : null}
    </div>
  );
};

export default PhoneInput;